import { CommonModule } from '@angular/common';
import {
  ChangeDetectorRef,
  Component,
  ElementRef,
  input,
  InputSignal,
  OnDestroy,
  OnInit,
  output,
  OutputEmitterRef,
  ViewChild,
} from '@angular/core';
import { TreeData } from '../../../../workspace-layout/commons/components/tree-selector/tree-selector';
import { EmbedService } from '../../services/embed.service';
import { Subject, takeUntil } from 'rxjs';
import { CanvasControllerService } from 'src/app/core/services/canvas-controller/canvas-controller.service';
import gsap from 'gsap';
import { ThreeGraphqlService } from 'src/app/core/graphql/three/services/three-graphql.service';
import { EmbedData } from '../../../../edit-layout/common/data/embed';
import { ThreedProject } from '../../../../workspace-layout/commons/services/project-controller/project-controller';

@Component({
  selector: 'embed-color-list',
  imports: [CommonModule],
  templateUrl: './embed-color-list.component.html',
  styleUrl: './embed-color-list.component.scss',
})
export class EmbedColorListComponent implements OnInit, OnDestroy {
  @ViewChild('colorListEl') colorListEl: ElementRef<HTMLDivElement> | undefined;
  public colors: InputSignal<Array<string>> = input(new Array<string>());
  public changeSelectedMaterialNames: OutputEmitterRef<Array<string>> = output();

  public tree: Array<any> = [];
  public selectedTree: Array<Array<TreeData>> = [];
  public embedData: EmbedData | undefined;

  public selectedProjectIndex: number = 0;

  private unsubscribeAll: Subject<any> = new Subject();

  constructor(
    private threeGraphqlService: ThreeGraphqlService,
    private embedService: EmbedService,
    private canvasController: CanvasControllerService,
    private cd: ChangeDetectorRef,
  ) {}

  //--------------------------------------------------------------------------------------------------------
  // @ Life Circle
  //--------------------------------------------------------------------------------------------------------

  ngOnInit(): void {
    this.getEmbedData();
    this.getSelectedProjectIndex();
  }

  ngOnDestroy(): void {
    this.unsubscribeAll.next(null);
    this.unsubscribeAll.complete();
    this.unsubscribeAll.unsubscribe();
  }

  //--------------------------------------------------------------------------------------------------------
  // @ Public Method
  //--------------------------------------------------------------------------------------------------------

  public onClickColor(color: string): void {
    this.canvasController.materialsColorChange(
      this.tree[this.tree.length - 1].map((item: any) => item.name),
      color,
    );
  }

  public onClickBack(): void {
    let gsapData = {
      left: this.colorListEl?.nativeElement.scrollLeft!,
    };
    let scrollLeft = this.colorListEl?.nativeElement.scrollLeft!;
    if (typeof scrollLeft == undefined) return;
    if (scrollLeft - 100 < 0) scrollLeft = 0;
    else scrollLeft -= 100;
    gsap.to(gsapData!, {
      left: scrollLeft,
      duration: 0.5,
      ease: 'power2.out',
      onUpdate: () => {
        this.colorListEl!.nativeElement.scrollLeft = gsapData.left;
      },
    });
    this.cd.markForCheck();
  }

  public onClickNext(): void {
    let gsapData = {
      left: this.colorListEl?.nativeElement.scrollLeft!,
    };
    let scrollLeft = this.colorListEl?.nativeElement.scrollLeft!;
    let scrollWidth = this.colorListEl?.nativeElement.scrollWidth;
    if (typeof scrollLeft == undefined) return;
    if (!scrollWidth) return;
    if (scrollLeft + 100 > scrollWidth) scrollLeft = scrollWidth;
    else scrollLeft += 100;
    gsap.to(gsapData!, {
      left: scrollLeft,
      duration: 0.5,
      ease: 'power2.out',
      onUpdate: () => {
        this.colorListEl!.nativeElement.scrollLeft = gsapData.left;
      },
    });
    this.cd.markForCheck();
  }

  //--------------------------------------------------------------------------------------------------------
  // @ Private Method
  //--------------------------------------------------------------------------------------------------------

  private traverseTree(child: any, index: number): void {
    if (!this.tree[index]) {
      this.tree[index] = [];
      if (index == 0) {
        this.selectedTree[index] = [child];
        this.selectedTree[index + 1] = [...child.child];
        this.changeSelectedMaterialNames.emit(this.selectedTree[index + 1].map((i) => i.name));
      }
    }
    this.tree[index].push(child);
    if (child.child.length > 0)
      child.child.forEach((item: TreeData) => this.traverseTree(item, index + 1));

    this.cd.markForCheck();
  }

  private getEmbedData(): void {
    this.embedService.embedData$.pipe(takeUntil(this.unsubscribeAll)).subscribe((embedData) => {
      if (!embedData) return;
      this.embedData = embedData;
      this.getSelectedProject(this.selectedProjectIndex);
      this.cd.markForCheck();
    });
  }

  private getSelectedProjectIndex(): void {
    this.embedService.selectedProjectIndex$
      .pipe(takeUntil(this.unsubscribeAll))
      .subscribe((index) => {
        this.selectedProjectIndex = index;
        this.getSelectedProject(index);
      });
  }

  private getSelectedProject(index: number): void {
    this.tree = [];
    this.selectedTree = [];
    if (!this.embedData) return;
    const project: ThreedProject = this.embedData.projects[index];
    if (!project) return;
    if (!project.baseModels) return;
    if (!project.baseModels[0]) return;
    this.traverseTree(project.baseModels[0], 0);
    this.cd.markForCheck();
  }
}
